import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import { getHealthStatus, type HealthStatus } from '../api/status';

function StatusDot({ status }: { status: string }) {
  const color =
    status === 'healthy' || status === 'connected'
      ? 'var(--accent-green)'
      : status === 'unknown'
        ? 'var(--accent-warning)'
        : 'var(--accent-red)';

  return (
    <span
      className="status-dot"
      style={{
        display: 'inline-block',
        width: 12,
        height: 12,
        borderRadius: '50%',
        background: color,
        marginRight: 8,
        flexShrink: 0,
      }}
    />
  );
}

function StatusRow({ label, status }: { label: string; status: string }) {
  return (
    <div className="settings-card__row">
      <span className="settings-card__label" style={{ display: 'flex', alignItems: 'center' }}>
        <StatusDot status={status} />
        {label}
      </span>
      <span className="settings-card__value" style={{ textTransform: 'capitalize' }}>
        {status}
      </span>
    </div>
  );
}

export function StatusView() {
  const { getJwtToken } = useAuth();
  const [health, setHealth] = useState<HealthStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchStatus = useCallback(async () => {
    try {
      const token = await getJwtToken();
      if (!token) return;
      const data = await getHealthStatus(token);
      setHealth(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch status');
    } finally {
      setLoading(false);
    }
  }, [getJwtToken]);

  useEffect(() => {
    fetchStatus();
    intervalRef.current = setInterval(fetchStatus, 30_000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchStatus]);

  return (
    <div>
      <div className="view__header">
        <h1 className="view__title">System Status</h1>
        <div className="view__actions">
          <button className="btn btn--secondary" onClick={fetchStatus} disabled={loading} type="button">
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert--error">
          <span>{error}</span>
          <button onClick={() => setError(null)} type="button">Dismiss</button>
        </div>
      )}

      {loading && !health && (
        <div className="view__empty">Checking system health...</div>
      )}

      {health && (
        <>
          {/* Last checked */}
          <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16 }}>
            Last checked: {new Date(health.checkedAt).toLocaleString()} | Auto-refreshes every 30s
          </p>

          {/* Overview KPIs */}
          <div className="dashboard-kpis">
            <div className="dashboard-kpi">
              <span className="dashboard-kpi__value" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <StatusDot status={health.gateway} />
                {health.gateway === 'healthy' ? 'Up' : 'Down'}
              </span>
              <span className="dashboard-kpi__label">Gateway</span>
            </div>
            <div className="dashboard-kpi">
              <span className="dashboard-kpi__value">{health.uptime}</span>
              <span className="dashboard-kpi__label">Uptime</span>
            </div>
            <div className="dashboard-kpi">
              <span className="dashboard-kpi__value">{health.version}</span>
              <span className="dashboard-kpi__label">Version</span>
            </div>
            <div className="dashboard-kpi">
              <span className="dashboard-kpi__value">{health.models.length}</span>
              <span className="dashboard-kpi__label">Models</span>
            </div>
          </div>

          {/* Core Services */}
          <section className="view__section">
            <h2 className="view__section-title">Core Services</h2>
            <div className="settings-card">
              <StatusRow label="Gateway" status={health.gateway} />
              <StatusRow label="Database" status={health.database} />
              <StatusRow label="Cache" status={health.cache} />
            </div>
          </section>

          {/* Model Health */}
          <section className="view__section">
            <h2 className="view__section-title">Model Health</h2>
            {health.models.length === 0 ? (
              <div className="settings-card settings-card--empty">
                No model health data available
              </div>
            ) : (
              <div className="settings-card">
                {health.models.map((m, idx) => (
                  <StatusRow key={`${m.model}-${idx}`} label={m.model} status={m.status} />
                ))}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
